/**
 * useRTDBManagerInit - Initialize persistent RTDB listener managers
 *
 * Call this hook once in AuthenticatedLayout to set up the listener managers.
 * This ensures managers persist across page navigation.
 *
 * The managers handle:
 * - Lazy subscription (only subscribe when user visits a location)
 * - Session persistence (listeners stay active until tab closes)
 * - Efficient delta updates (no full re-syncs on location switch)
 */

import { useEffect, useRef } from 'react';
import { useFirebaseStore } from '../../store/firebaseStore';
import { useRTDBCacheStore } from '../../store/rtdbCacheStore';
import { getRentalRTDBPath, getStampsRTDBPath } from '../../config/collections';
import { logger } from '../../utils/logger';
import type { AnyRentalItem } from '@/types';

// Import categories from actual config files (single source of truth)
import { RENTALS_CONFIG } from '@/unified-rental-board/configs/rentals.config';
import { STAMPS_CONFIG } from '@/unified-rental-board/configs/stamps.config';

// Use categories from configs
const RENTALS_CATEGORIES = RENTALS_CONFIG.categories;
const STAMPS_CATEGORIES = STAMPS_CONFIG.categories;

/**
 * Parse RTDB data into typed items array.
 * RTDB returns an object keyed by document ID.
 *
 * IMPORTANT: This parser must validate data structure to prevent runtime crashes.
 * Invalid items are logged and skipped rather than crashing the entire board.
 */
function parseRTDBData(data: unknown): AnyRentalItem[] {
  if (!data) return [];

  const items: AnyRentalItem[] = [];
  const entries = Object.entries(data as Record<string, unknown>);

  for (const [id, rawData] of entries) {
    if (!rawData || typeof rawData !== 'object') continue;

    const item = rawData as Record<string, unknown>;

    // Basic validation - skip items without required fields
    if (!item.category || !item.type) continue;

    // Type-specific validation
    if (item.type === 'item') {
      // Validate rental equipment item has required fields
      if (!item.description || typeof item.equipmentId !== 'string' || !item.sets) {
        logger.error(`[RTDB] Item ${id} missing required fields:`, item);
        continue;
      }

      // Validate sets array structure
      if (!Array.isArray(item.sets) || item.sets.length !== 4) {
        logger.error(`[RTDB] Item ${id} has invalid sets array:`, item.sets);
        continue;
      }

      // Validate each set has status (prevent "status.toUpperCase() is undefined" crash)
      const hasInvalidSet = item.sets.some(
        (set: unknown) => !set || typeof (set as Record<string, unknown>).status !== 'string'
      );
      if (hasInvalidSet) {
        logger.error(`[RTDB] Item ${id} has invalid set structure (missing status):`, item.sets);
        continue;
      }
    } else if (item.type === 'header') {
      // Validate header has content array
      if (!Array.isArray(item.content) || item.content.length !== 6) {
        logger.error(`[RTDB] Header ${id} has invalid content array:`, item.content);
        continue;
      }
    } else if (item.type === 'multiples') {
      // Validate multiples item has required fields
      if (!item.description || typeof item.totalQuantity !== 'number') {
        logger.error(`[RTDB] Multiples item ${id} missing required fields:`, item);
        continue;
      }
    } else if (item.type !== 'separator') {
      // Unknown type - skip
      logger.error(`[RTDB] Unknown item type for ${id}:`, item.type);
      continue;
    }

    items.push({
      ...item,
      id,
    } as AnyRentalItem);
  }

  return items;
}

/**
 * Initialize RTDB listener managers.
 * Call once in AuthenticatedLayout when Firebase is ready.
 */
export function useRTDBManagerInit(): void {
  const rtdb = useFirebaseStore((state) => state.rtdb);
  const isConnected = useFirebaseStore((state) => state.isConnected);
  const initializeManagers = useRTDBCacheStore((state) => state.initializeManagers);

  // Use ref to track if we've initialized - this is the ONLY guard against re-init
  // We intentionally don't subscribe to isInitialized to avoid re-render loops
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Wait for RTDB to be ready
    if (!rtdb || !isConnected) {
      return;
    }

    // Don't re-initialize if already done
    if (hasInitializedRef.current) {
      return;
    }

    logger.log('[RTDB] Initializing persistent listener managers');
    hasInitializedRef.current = true;

    initializeManagers(
      rtdb,
      {
        getPath: getRentalRTDBPath,
        parseData: parseRTDBData,
        categories: RENTALS_CATEGORIES,
      },
      {
        getPath: getStampsRTDBPath,
        parseData: parseRTDBData,
        categories: STAMPS_CATEGORIES,
      }
    );

    // NOTE: No cleanup function here intentionally.
    // Firebase automatically detects disconnection via WebSocket heartbeat.
    // Adding cleanup caused infinite re-render loops due to Zustand state updates.
  }, [rtdb, isConnected, initializeManagers]);
}
